import React, {useState} from 'react';
import {Button} from 'primereact/button';
import {useAppSelector} from '../../../../redux/hooks';
import './CampaignLeadsStats.scss';

const LEADS_PHONE_TYPE_ORDER = [
    'Контакти клієнта',
    'Адмін',
    'Клієнт',
    'УБКІ',
    'МБКІ',
    'БКІ УКБ',
];

const COINCIDENCE_LABEL = 'Співпадіння';
const EMPTY_LEADS_METRICS = {
    leads: '0',
    active: '0',
    inactive: '0',
    attempts: '0/0',
    success: '0',
    unsuccess: '0',
};

const LEADS_METRIC_HEADERS = [
    {key: 'leads', label: 'Всього'},
    {key: 'active', label: 'Активних'},
    {key: 'inactive', label: 'Не активні'},
    {key: 'attempts', label: 'Спроби'},
    {key: 'success', label: 'Успішні'},
    {key: 'unsuccess', label: 'Не успішні'},
] as const;

const STATISTICS_METRIC_HEADERS = [
    {key: 'total', label: 'Всього'},
    {key: 'active', label: 'Активні'},
    {key: 'success', label: 'Успішні'},
    {key: 'unsuccess', label: 'Не успішні'},
] as const;

const STATISTICS_GROUPS = [
    {
        id: 'inbound',
        title: 'Вхідні',
        summaryName: 'Загально',
        itemsKey: 'internal',
        totalKey: 'internalTotal',
        defaultCallGroups: [
            'InternalIvr',
            'InternalQueue',
            'InternalPickupCall',
            'InternalCallBack',
            'InternalTransfer',
            'InternalConsultation',
            'InternalP2P',
        ],
    },
    {
        id: 'outbound',
        title: 'Вихідні',
        summaryName: 'Загально',
        itemsKey: 'external',
        totalKey: 'externalTotal',
        defaultCallGroups: [
            'ExternalOutbound',
            'ExternalC2C',
            'ExternalCallBack',
            'ExternalTransfer',
            'ExternalConsultation',
            'ExternalQueue',
            'ExternalP2P',
            'ExternalSpyCall',
        ],
    },
    {
        id: 'campaigns',
        title: 'Кампанії',
        summaryName: 'Загально',
        itemsKey: 'campaigns',
        totalKey: 'campaignsTotal',
        defaultCallGroups: [
            'CampaignExternal',
            'CampaignIvm',
        ],
    },
] as const;

const CALL_GROUP_LABELS: Record<string, string> = {
    CampaignExternal: 'Progressive',
    CampaignIvm: 'IVM',
    InternalCallBack: 'Callback',
    InternalConsultation: 'Consultation',
    InternalIvr: 'IVR',
    InternalP2P: 'P2P',
    InternalPickupCall: 'PickupCall',
    InternalQueue: 'Queue',
    InternalTransfer: 'Transfer',
    ExternalC2C: 'C2C',
    ExternalCallBack: 'Callback',
    ExternalConsultation: 'Consultation',
    ExternalOutbound: 'Outbound',
    ExternalP2P: 'P2P',
    ExternalQueue: 'Queue',
    ExternalSpyCall: 'Spycall',
    ExternalTransfer: 'Transfer',
};

const EMPTY_STATISTICS_METRICS = {
    total: '0',
    active: '0',
    success: '0',
    unsuccess: '0',
};

const toStatisticsMetrics = (item: any) => ({
    total: String(item?.total ?? 0),
    active: String(item?.active ?? 0),
    success: String(item?.success ?? 0),
    unsuccess: String(item?.unsuccess ?? 0),
});

const getCallGroupLabel = (callGroup: string) => {
    if (!callGroup) return '';
    const labelByKey = CALL_GROUP_LABELS[callGroup]
        || Object.entries(CALL_GROUP_LABELS).find(([key]) => key.toLowerCase() === callGroup.toLowerCase())?.[1];
    if (labelByKey) return labelByKey;
    return callGroup.replace(/^(Campaign|Internal|External)/i, '');
};

const METRIC_VALUE_COLORS: Record<string, string> = {
    leads: '#0F8BFD',
    attempts: '#0F8BFD',
    total: '#0F8BFD',
    active: '#2ABE12',
    inactive: '#e0e0e0',
    success: '#00a0b4',
    unsuccess: '#FF0000',
};

const renderMetricValue = (metricKey: string, value: string) => {
    if (value == null || value === '') return null;
    const color = METRIC_VALUE_COLORS[metricKey];
    const match = String(value).match(/^(\d+)\s*\/\s*(\d+)$/);
    if (match) {
        const isAttempts = metricKey === 'attempts';
        return (
            <>
                <span style={{color: isAttempts ? 'var(--text-color)' : color}}>{match[1]}</span>
                <span style={{color, opacity: isAttempts ? 1 : 0.48}}> / {match[2]}</span>
            </>
        );
    }
    return <span style={{color}}>{value}</span>;
};

const getLeadsDashboardMetrics = (item: any) => {
    if (!item) return EMPTY_LEADS_METRICS;
    const active = Number(item.active ?? 0);
    const activeTotal = Number(item.activeTotal ?? 0);
    const inactive = Math.max(activeTotal - active, 0);
    return {
        leads: String(activeTotal ?? 0),
        active: String(active),
        inactive: String(inactive),
        attempts: `${item.attemptsDone ?? 0}/${item.attemptsTotal ?? 0}`,
        success: String(item.success ?? 0),
        unsuccess: String(item.unsuccess ?? 0),
    };
};

const isLeadsDashboardTotal = (item: any) => {
    const name = (item?.phoneTypeName || item?.name || '').toString().trim().toUpperCase();
    return item?.phoneTypeId == null && name === 'TOTAL';
};

const asArray = (value: any) => {
    if (Array.isArray(value)) return value;
    if (value && typeof value === 'object') return Object.values(value);
    return [];
};

const CampaignLeadsStats = (props: {campaignContacts?: any; variant?: 'leads' | 'statistics' | 'monitoring'}) => {
    const variant = props.variant || 'leads';
    const isExtended = variant === 'statistics' || variant === 'monitoring';
    const [expanded, setExpanded] = useState(false);
    const colorScheme = useAppSelector(state => state.ThemeReducer.colorScheme);
    const statisticDashboard = useAppSelector(state => state.StatisticsListReducer.statisticDashboard);
    const monitoringDashboard = useAppSelector(state => state.MonitoringListReducer.monitoringDashboard);
    const campaignLeadsDashboard = useAppSelector(state => state.CompaniesListReducer.campaignLeadsDashboard);
    const statisticDashboardLoading = useAppSelector(state => state.StatisticsListReducer.statisticDashboardLoading);
    const monitoringDashboardLoading = useAppSelector(state => state.MonitoringListReducer.monitoringDashboardLoading);
    const campaignLeadsDashboardLoading = useAppSelector(state => state.CompaniesListReducer.campaignLeadsDashboardLoading);
    const dashboard = variant === 'monitoring' ? monitoringDashboard : statisticDashboard;
    const dashboardLoading = variant === 'monitoring'
        ? !!monitoringDashboardLoading
        : variant === 'statistics'
            ? !!statisticDashboardLoading
            : !!campaignLeadsDashboardLoading;
    const campaignContacts = props.campaignContacts;
    const summarySource = campaignContacts?.all || campaignContacts?.statistic || campaignContacts?.summary || null;
    const typeStats = asArray(
        campaignContacts?.phoneTypes
        || campaignContacts?.types
        || campaignContacts?.phoneTypeStatistic
        || summarySource?.phoneTypes
        || summarySource?.types
        || summarySource?.phoneTypeStatistic
    );
    const isLight = colorScheme === 'light';

    const dashboardItems = asArray(campaignLeadsDashboard);
    const totalDashboardItem = dashboardItems.find(isLeadsDashboardTotal);
    const typeDashboardItems = dashboardItems.filter((item: any) => !isLeadsDashboardTotal(item));
    const typeItemsById = typeDashboardItems.reduce((acc: Record<string, any>, item: any) => {
        if (item?.phoneTypeId != null) acc[String(item.phoneTypeId)] = item;
        return acc;
    }, {});
    const typeItemsByName = typeDashboardItems.reduce((acc: Record<string, any>, item: any) => {
        const name = (item?.phoneTypeName || '').toString().trim().toLowerCase();
        if (name) acc[name] = item;
        return acc;
    }, {});

    const findTypeStat = (name: string, phoneTypeId?: number | null) => {
        if (phoneTypeId != null && typeItemsById[String(phoneTypeId)]) return typeItemsById[String(phoneTypeId)];
        const itemName = (name || '').toString().trim().toLowerCase();
        if (itemName && typeItemsByName[itemName]) return typeItemsByName[itemName];
        return typeStats.find((item: any) => {
            const itemId = item?.phoneTypeId ?? item?.typeId ?? item?.dictionaryId ?? item?.id;
            const statName = (item?.name || item?.phoneTypeName || item?.typeName || '').toString().trim().toLowerCase();
            if (phoneTypeId != null && Number(itemId) === Number(phoneTypeId)) return true;
            return statName === name.trim().toLowerCase();
        }) || null;
    };

    const arrivedTypeRows = typeDashboardItems
        .map((item: any) => {
            const name = (item?.phoneTypeName || item?.name || '').toString().trim();
            if (!name || name.toUpperCase() === 'TOTAL') return null;
            return {
                name,
                metrics: getLeadsDashboardMetrics(item),
            };
        })
        .filter((item): item is {name: string; metrics: ReturnType<typeof getLeadsDashboardMetrics>} => !!item);

    const placeholderTypeRows = LEADS_PHONE_TYPE_ORDER.map((name) => ({
        name,
        metrics: getLeadsDashboardMetrics(findTypeStat(name)),
    }));

    const typeRows = arrivedTypeRows.length ? arrivedTypeRows : placeholderTypeRows;
    const hasCoincidenceRow = typeRows.some((row) => row.name.trim().toLowerCase() === COINCIDENCE_LABEL.toLowerCase());

    const leadsRows = [
        {
            name: 'Ліди',
            metrics: getLeadsDashboardMetrics(totalDashboardItem),
        },
        ...(expanded ? [
            ...typeRows,
            ...(!hasCoincidenceRow ? [{
                name: COINCIDENCE_LABEL,
                metrics: getLeadsDashboardMetrics(findTypeStat(COINCIDENCE_LABEL)),
            }] : []),
        ] : []),
    ];

    const statisticsGroups = STATISTICS_GROUPS.map((group) => {
        const items = asArray(dashboard?.[group.itemsKey]);
        const itemsByGroup = items.reduce((acc: Record<string, any>, item: any) => {
            const callGroup = item?.callGroup;
            if (callGroup) acc[callGroup.toLowerCase()] = item;
            return acc;
        }, {});
        const knownKeys = new Set((group.defaultCallGroups as readonly string[]).map((key) => key.toLowerCase()));
        const extraItems = items.filter((item: any) => item?.callGroup && !knownKeys.has(item.callGroup.toLowerCase()));
        const totalSource = dashboard?.[group.totalKey];
        return {
            ...group,
            summaryMetrics: totalSource ? toStatisticsMetrics(totalSource) : EMPTY_STATISTICS_METRICS,
            rows: [
                ...group.defaultCallGroups.map((callGroup) => ({
                    key: callGroup,
                    name: getCallGroupLabel(callGroup),
                    metrics: toStatisticsMetrics(itemsByGroup[callGroup.toLowerCase()] || EMPTY_STATISTICS_METRICS),
                })),
                ...extraItems.map((item: any) => ({
                    key: item.callGroup,
                    name: getCallGroupLabel(item.callGroup),
                    metrics: toStatisticsMetrics(item),
                })),
            ],
        };
    });

    const toggleButton = (
        <Button
            type="button"
            icon={expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
            className="p-button-secondary campaign-leads-stats__toggle"
            onClick={() => setExpanded((prev) => !prev)}
            aria-label={expanded ? 'Згорнути статистику' : 'Розгорнути статистику'}
        />
    );

    return (
        <div className={`campaign-leads-stats campaign-leads-stats--${isExtended ? 'extended' : 'compact'} ${isLight ? 'campaign-leads-stats--light' : ''} ${expanded ? 'campaign-leads-stats--expanded' : ''} ${dashboardLoading ? 'campaign-leads-stats--loading' : ''}`}>
            {toggleButton}
            <div className="campaign-leads-stats__panel">
                {isExtended ? (
                    statisticsGroups.map((group) => (
                        <table key={group.id} className="campaign-leads-stats__table">
                            <thead>
                                <tr>
                                    <th className={`campaign-leads-stats__name-col campaign-leads-stats__group-title campaign-leads-stats__group-title--${group.id}`}>{group.title}</th>
                                    {STATISTICS_METRIC_HEADERS.map((metric) => (
                                        <th key={metric.key}>{metric.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="campaign-leads-stats__name-col">{group.summaryName}</td>
                                    {STATISTICS_METRIC_HEADERS.map((metric) => (
                                        <td key={`${group.id}-summary-${metric.key}`}>
                                            {renderMetricValue(metric.key, group.summaryMetrics[metric.key])}
                                        </td>
                                    ))}
                                </tr>
                                {expanded && group.rows.map((row) => (
                                    <tr key={`${group.id}-${row.key}`}>
                                        <td className="campaign-leads-stats__name-col">{row.name}</td>
                                        {STATISTICS_METRIC_HEADERS.map((metric) => (
                                            <td key={`${group.id}-${row.name}-${metric.key}`}>
                                                {renderMetricValue(metric.key, row.metrics[metric.key])}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ))
                ) : (
                    <table className="campaign-leads-stats__table">
                        <thead>
                            <tr>
                                <th className="campaign-leads-stats__name-col"></th>
                                {LEADS_METRIC_HEADERS.map((metric) => (
                                    <th key={metric.key}>{metric.label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {leadsRows.map((row) => (
                                <tr key={row.name}>
                                    <td className="campaign-leads-stats__name-col">{row.name}</td>
                                    {LEADS_METRIC_HEADERS.map((metric) => (
                                        <td key={`${row.name}-${metric.key}`}>
                                            {renderMetricValue(metric.key, row.metrics[metric.key])}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default CampaignLeadsStats;
